import { useQuery } from '@tanstack/react-query';
import { authService } from '../services/api';
import { queryKeys } from '../lib/react-query';
import type { User } from '../types/api';

/**
 * Centralized current user hook using React Query.
 * - Shares a single /auth/me request across the app via cache (dedupes calls).
 * - Enabled only when a token exists.
 * - Avoids retry on 401/403 to prevent infinite refresh loops.
 * - Refetches on window focus to ensure fresh data.
 */
export function useCurrentUser() {
  const enabled = authService.isAuthenticated();

  const query = useQuery<User, any>({
    queryKey: queryKeys.auth.me,
    queryFn: () => authService.getCurrentUser(),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes (réduit pour des données plus fraîches)
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Rafraîchir quand la fenêtre redevient active
    refetchOnMount: true, // Rafraîchir au montage du composant
    retry: (failureCount, error: any) => {
      const status = error?.response?.status ?? error?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });

  return {
    user: (query.data as User) ?? null,
    isLoading: query.isLoading || query.isFetching,
    isAuthenticated: enabled && !!query.data,
    error: query.error ? (query.error as any).message ?? 'Erreur lors du chargement utilisateur' : null,
    refetch: query.refetch,
  };
}